import React from 'react';

// Filters employee data based on search terms and alert status filters
export const filterData = (data, searchTerm, alertFilters, censusYearData) => {
  if (!data || !data.Row) return { Row: {} };
  
  const lowercasedFilter = searchTerm ? searchTerm.toLowerCase() : '';
  const searchParts = lowercasedFilter.split(" ");

  const filteredRows = Object.entries(data.Row).filter(([key, employee]) => {
    const terminationDate = employee.DateOfTermination?.FinalValue;
    const hireDate = employee.DateOfHire?.FinalValue;

    const hasErrorStatus = Object.values(employee).some(field => 
      ['Error', 'Invalid'].includes(field?.FinalValue?.Status)
    );
    
    const hasValidGrossCompensation = parseFloat(employee.GrossCompensation?.FinalValue?.Value) >= 0;
    const hasValidHoursWorked = parseFloat(employee.HoursWorked?.FinalValue?.Value) >= 0;

    // Keep terminated employees visible if they have errors or were rehired
    if (terminationDate && terminationDate.Source === "PriorYear") {
      if (
        hireDate?.Value?.toLowerCase() === "conflict" ||
        hireDate?.Value?.toLowerCase() === "rehire" ||
        hasErrorStatus ||
        new Date(terminationDate.Value) >= new Date(censusYearData.current_year_start_date)
      ) {
        // Stay visible
      } else {
        return false;
      }
    }

    const matchField = (field) => {
      const finalValueMatch = field?.FinalValue?.Value?.toString().toLowerCase().includes(lowercasedFilter);
      const valuesArrayMatch = field?.Values?.some(valueObj =>
        valueObj.Value?.toString().toLowerCase().includes(lowercasedFilter)
      );
      return finalValueMatch || valuesArrayMatch;
    };

    let matchesSearchTerm = true;
    if (searchTerm) {
      matchesSearchTerm = false;

      // Handle full name search (first and last name)
      if (searchParts.length > 1) {
        const firstNameMatch = employee.FirstName && (
          employee.FirstName?.FinalValue?.Value?.toLowerCase().includes(searchParts[0]) ||
          employee.FirstName?.Values?.some(valueObj => valueObj.Value?.toLowerCase().includes(searchParts[0]))
        );

        const lastNameMatch = employee.LastName && (
          employee.LastName?.FinalValue?.Value?.toLowerCase().includes(searchParts[1]) ||
          employee.LastName?.Values?.some(valueObj => valueObj.Value?.toLowerCase().includes(searchParts[1]))
        );

        if (firstNameMatch && lastNameMatch) {
          matchesSearchTerm = true;
        }
      }

      // Fallback to checking individual fields
      if (!matchesSearchTerm) {
        matchesSearchTerm = Object.entries(employee).some(([fieldKey, field]) => {
          if (fieldKey === 'FirstName' || fieldKey === 'LastName') {
            return matchField(field);
          } else {
            return field?.FinalValue?.Value?.toString().toLowerCase().includes(lowercasedFilter);
          }
        });
      }
    }

    const matchesAlertFilter = !alertFilters || alertFilters.length === 0 || Object.values(employee).some(field => {
      return alertFilters.includes(field?.FinalValue?.Status);
    });

    return matchesSearchTerm && matchesAlertFilter;
  });

  return { Row: Object.fromEntries(filteredRows) };
};

// Generates SHA-256 hash for employee key
export async function generateOriginalKey(SSN, firstName, lastName, dateOfBirth, customerNumber) {
  firstName = (firstName || '').trim();
  lastName = (lastName || '').trim();
  customerNumber = String(customerNumber || '').trim();

  let standardizedSSN;
  if (SSN && SSN.trim()) {
    standardizedSSN = SSN.replace(/-/g, '');
  } else {
    const dobString = dateOfBirth ? new Date(dateOfBirth).toISOString().split('T')[0] : '';
    standardizedSSN = `${firstName}${lastName}${dobString}`;
  }

  const inputString = `${customerNumber}${standardizedSSN}`;
  const encoder = new TextEncoder();
  const data = encoder.encode(inputString);

  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');

  return hashHex;
}

// Calculates estimated hours based on Guidant's methodology
export const calculateGuidantEstimatedHours = (dateOfHire, dateOfTermination, planYearStart, planYearEnd) => {
  const convertToISO = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateStr;
    }
    if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) {
      const [month, day, year] = dateStr.split('-');
      return `${year}-${month}-${day}`;
    }
    return dateStr;
  };
  
  const parseLocalDate = (dateStr) => {
    const isoDate = convertToISO(dateStr);
    if (!isoDate) return null;
    return new Date(isoDate + 'T00:00:00');
  };
  
  const hireDate = parseLocalDate(dateOfHire);
  const termDate = parseLocalDate(dateOfTermination);
  const yearStart = parseLocalDate(planYearStart);
  const yearEnd = parseLocalDate(planYearEnd);
  
  const startDate = new Date(Math.max(hireDate, yearStart));
  const endDate = termDate ? new Date(Math.min(termDate, yearEnd)) : yearEnd;
  
  let totalHours = 0;
  let currentDate = new Date(startDate);
  
  while (currentDate <= endDate) {
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    const isFirstMonth = currentDate.getTime() === startDate.getTime();
    const isLastMonth = currentDate.getMonth() === endDate.getMonth() && 
                       currentDate.getFullYear() === endDate.getFullYear();
    
    if (isFirstMonth && startDate.getDate() >= 16) {
      totalHours += 95;
    } else if (isLastMonth && dateOfTermination && endDate.getDate() <= 15) {
      totalHours += 95;
    } else {
      totalHours += 190;
    }
    
    currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
  }
  
  return totalHours.toString();
};

export const normalizeToDecimal = (value) => {
  if (!value) return '0.00';
  const cleanedValue = value.replace(/[$,]/g, '');
  const normalizedValue = parseFloat(cleanedValue).toFixed(2);
  return normalizedValue;
};

export const capitalizeFirstLetter = (string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const defaultFieldData = () => ({
  Values: [{ Value: '', Source: 'manual_input' }],
  FinalValue: { Value: '', Source: 'manual_input', Status: '', Message: '' }
});

// Determines census URL based on environment
export const getBaseUrl = () => {
  const currentHostname = window.location.hostname;

  if (
    currentHostname === 'localhost' || 
    currentHostname === '127.0.0.1' ||
    currentHostname === 'censusupload-dev.guidantfinancial.com' ||
    currentHostname === 'guidant-census-data.azurewebsites.net'
  ) {
    return 'https://census-dev.guidantfinancial.com/EmployeeStatus';
  } else if (
    currentHostname === 'censusupload-stg.guidantfinancial.com' ||
    currentHostname === 'guidant-census-data-stg.azurewebsites.net'
  ) {
    return 'https://census-stg.guidantfinancial.com/EmployeeStatus';
  } else if (
    currentHostname === 'censusupload.guidantfinancial.com' ||
    currentHostname === 'guidant-census-data-prod.azurewebsites.net'
  ) {
    return 'https://census.guidantfinancial.com/EmployeeStatus';
  } else {
    throw new Error('Unknown environment: Unable to determine census URL');
  }
};

export function formatDate(dateString) {
  if (!dateString) return '';

  const [year, month, day] = dateString.split('-');
  const date = new Date(Date.UTC(year, month - 1, day));

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).toUpperCase();
}

export const normalizeEmployeeFields = (editableData) => {
  // Names
  const FirstName = capitalizeFirstLetter(editableData.FirstName);
  const MiddleName = capitalizeFirstLetter(editableData.MiddleName);
  const LastName = capitalizeFirstLetter(editableData.LastName);
  
  // Numeric fields
  const GrossCompensation = editableData.GrossCompensation ? normalizeToDecimal(editableData.GrossCompensation) : editableData.GrossCompensation;
  const HoursWorked = editableData.HoursWorked ? normalizeToDecimal(editableData.HoursWorked) : editableData.HoursWorked;
  const DeferALS = editableData.DeferALS ? normalizeToDecimal(editableData.DeferALS) : editableData.DeferALS;
  const ParticipantRoth = editableData.ParticipantRoth ? normalizeToDecimal(editableData.ParticipantRoth) : editableData.ParticipantRoth;
  const CompanyMatch = editableData.CompanyMatch ? normalizeToDecimal(editableData.CompanyMatch) : editableData.CompanyMatch;
  const CompanyProfitSharing = editableData.CompanyProfitSharing ? normalizeToDecimal(editableData.CompanyProfitSharing) : editableData.CompanyProfitSharing;
  const AnnualLoanPayment = editableData.AnnualLoanPayment ? normalizeToDecimal(editableData.AnnualLoanPayment) : editableData.AnnualLoanPayment;
  const RollOver = editableData.RollOver ? normalizeToDecimal(editableData.RollOver) : editableData.RollOver;
  
  // Date fields
  const DateOfHire = editableData.DateOfHire ? editableData.DateOfHire.replace(/\//g, '-') : editableData.DateOfHire;
  const DateOfBirth = editableData.DateOfBirth ? editableData.DateOfBirth.replace(/\//g, '-') : editableData.DateOfBirth;
  const DateOfTermination = editableData.DateOfTermination ? editableData.DateOfTermination.replace(/\//g, '-') : editableData.DateOfTermination;
  const DateOfReHire = editableData.DateOfReHire ? editableData.DateOfReHire.replace(/\//g, '-') : editableData.DateOfReHire;
  
  // Return all fields including unchanged ones
  return {
    FirstName,
    LastName,
    MiddleName,
    DateOfBirth,
    DateOfHire,
    DateOfReHire,
    DateOfTermination,
    Status: editableData.Status,
    GrossCompensation,
    GrossCompensationNote: editableData.GrossCompensationNote,
    HoursWorked,
    HoursWorkedNote: editableData.HoursWorkedNote,
    DeferALS,
    ParticipantRoth,
    CompanyMatch,
    CompanyProfitSharing,
    AnnualLoanPayment,
    RollOver,
    SSN: editableData.SSN,
    originalKey: editableData.originalKey
  };
};

export const generateEmployeeKey = async (existingEmployee, oldKey, FirstName, LastName, DateOfBirth, SSN, customerNumber) => {
  let newKey = '';
  let isDuplicate = false;
  
  if (existingEmployee) {
    newKey = oldKey;
  } else {
    if (oldKey) {
      isDuplicate = oldKey.endsWith('_duplicate');
    }

    newKey = await generateOriginalKey(SSN, FirstName, LastName, DateOfBirth, customerNumber);

    if (isDuplicate) {
      newKey += '_duplicate';
    }
  }
  
  return newKey;
};